/**
 * ZoneTable's cell, row and header CLASS builders.
 *
 * Pure functions taking everything they need as arguments rather than reading `this`, because the
 * same rules have to produce the same classes from three different places — the header, the body
 * and the table shell — and a `<th>` and a `<td>` that disagree by a pixel or a z-index is exactly
 * the class of bug this file exists to prevent.
 *
 * `stickyOptions` is threaded through instead of read from props so that the header and the body
 * cannot drift: both pass the same object, so both get the same geometry.
 */
import type { FlatRow, RowClassContext, TableColumn, TableRow } from './types';

/**
 * Paint order for the cells that can be pinned at once. Split by region so the header always wins
 * over the body and the checkbox column always wins over the data column beside it — see
 * `stickyColumnClasses` for why each number is what it is.
 */
export type StickyRegion = 'body' | 'header';

export const STICKY_LAYERS: Record<StickyRegion, { firstColumn: string; checkbox: string }> = {
  body: { firstColumn: 'z-10', checkbox: 'z-20' },
  header: { firstColumn: 'z-30', checkbox: 'z-40' },
};

/** What the pinned-column geometry depends on, and nothing else. */
export interface StickyOptions {
  stickyFirstColumn: boolean;
  selectable: boolean;
}

export function alignClass(column: TableColumn): string {
  if (column.align === 'right') {
    return 'text-right';
  }
  if (column.align === 'center') {
    return 'text-center';
  }

  return 'text-left';
}

/**
 * The pinned first column. `left` clears the selection cell when there is one.
 *
 * Four sticky cells can meet, and they used to sit on TWO z-indexes with ties inside each — so
 * whichever came later in the DOM won, which is the body. Explicit layers instead, low to high
 * (see STICKY_LAYERS):
 *
 *   body first column   10   ← what unpinned columns scroll under
 *   body checkbox       20   ← left of, and above, the column beside it
 *   header first column 30   ← above every body cell, pinned or not
 *   header checkbox     40   ← the top-left corner, above all three
 *
 * When `stickyHeader` is on, the <thead> itself is sticky and therefore a stacking context, so
 * 30/40 are resolved inside it and the whole header still lands above the body's 10/20. When it is
 * off the <thead> is static, the header cells compete directly, and the same order holds. Right
 * either way.
 */
export function stickyColumnClasses(
  columnIndex: number,
  options: StickyOptions,
  region: StickyRegion = 'body',
): string {
  if (!options.stickyFirstColumn || columnIndex !== 0) {
    return '';
  }

  const left = options.selectable ? 'left-[42px]' : 'left-0';

  return `sticky ${left} ${STICKY_LAYERS[region].firstColumn}`;
}

/**
 * The 42px checkbox column, in either region.
 *
 * `bg-inherit`, not a literal colour: the body's copy has to pick up whatever tint its row carries
 * (hover, expanded, a caller's rowClass), and the header's picks up the header row's gray-50. Both
 * rows paint a background, so neither resolves to transparent — an unpainted row here is what made
 * columns scrolling under a pinned column show straight through it.
 */
export function selectionCellClasses(options: StickyOptions, region: StickyRegion): string {
  return [
    'w-[42px] border-b border-r border-gray-200 bg-inherit px-3 py-2 align-middle',
    options.stickyFirstColumn ? `sticky left-0 ${STICKY_LAYERS[region].checkbox}` : '',
  ].filter(Boolean).join(' ');
}

export function headerCellClasses(
  column: TableColumn,
  columnIndex: number,
  options: StickyOptions,
): string {
  return [
    // `group/header` sits on the whole <th> so hovering anywhere in the cell reveals the sort
    // arrow, not just the label.
    'group/header border-b border-r border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800 last:border-r-0',
    column.minWidth || 'min-w-[100px]',
    column.width || '',
    column.headerClass || '',
    alignClass(column),
    stickyColumnClasses(columnIndex, options, 'header'),
  ].filter(Boolean).join(' ');
}

export function bodyCellClasses(
  column: TableColumn,
  options: StickyOptions,
  columnIndex = 0,
  entry: FlatRow | null = null,
): string {
  return [
    'border-b border-r border-gray-200 bg-inherit px-3 py-2 align-middle text-sm font-medium text-gray-700 last:border-r-0',
    column.minWidth || 'min-w-[100px]',
    column.width || '',
    column.cellClass || '',
    alignClass(column),
    stickyColumnClasses(columnIndex, options),
    entry && entry.level > 0 && columnIndex === 0 ? 'text-gray-700' : '',
  ].filter(Boolean).join(' ');
}

export function cellContentClasses(column: TableColumn): string {
  return column.align === 'right' ? 'text-right' : '';
}

/**
 * Per the Zone spec ("when rows are expanded the entire row background changes to hover state"),
 * an EXPANDED row keeps the hover background so the open group reads as one block. Nested children
 * stay white — the design shows indentation alone carrying the hierarchy, and tinting them would
 * make a collapsed-parent row and its children look like different things.
 *
 * Every row paints an opaque background, which the pinned cells' `bg-inherit` depends on.
 *
 * Returns an ARRAY, not a joined string, so the caller's `rowClass` can hand back any of the shapes
 * Vue's class binding accepts — including the object form for conditional classes.
 */
export function rowClasses(
  entry: FlatRow,
  selected: boolean,
  rowClass: ((row: TableRow, context: RowClassContext) => unknown) | null,
): unknown[] {
  const classes: unknown[] = ['group/row'];

  classes.push(entry.expanded ? 'bg-gray-50' : 'bg-white');
  classes.push('hover:bg-gray-50');

  if (rowClass) {
    classes.push(rowClass(entry.row, {
      level: entry.level,
      index: entry.index,
      expanded: entry.expanded,
      selected,
    }));
  }

  return classes;
}

export function sortButtonClasses(active: boolean): string {
  return [
    'ml-auto inline-flex shrink-0 items-center text-gray-600 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
    // The spec asks for the sort affordance to appear on hover, and stay put once it is active.
    active ? 'opacity-100' : 'opacity-0 group-hover/header:opacity-100 focus-visible:opacity-100',
  ].join(' ');
}
